import React, { useEffect, useCallback } from 'react';
import { X } from 'lucide-react';
import './Modal.css';

/**
 * Modal Component
 *
 * @param {boolean} isOpen - Show/hide modal
 * @param {function} onClose - Close handler
 * @param {string} title - Modal title
 * @param {string} size - 'sm' | 'md' | 'lg' | 'xl' | 'full'
 * @param {boolean} closeOnOverlay - Close when clicking overlay
 * @param {boolean} closeOnEsc - Close on Escape key
 * @param {boolean} showClose - Show close button
 */
const Modal = ({
  isOpen,
  onClose,
  title,
  size = 'md',
  closeOnOverlay = true,
  closeOnEsc = true,
  showClose = true,
  children,
  footer,
  className = '',
}) => {
  // Handle escape key
  const handleEscape = useCallback((e) => {
    if (e.key === 'Escape' && closeOnEsc) {
      onClose();
    }
  }, [closeOnEsc, onClose]);

  // Handle overlay click
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget && closeOnOverlay) {
      onClose();
    }
  };

  // Add/remove event listeners and body scroll lock
  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleEscape]);

  if (!isOpen) return null;

  const modalClassNames = [
    'oz-modal',
    `oz-modal--${size}`,
    className
  ].filter(Boolean).join(' ');

  return (
    <div className="oz-modal-overlay" onClick={handleOverlayClick}>
      <div className={modalClassNames} role="dialog" aria-modal="true">
        {/* Header */}
        {(title || showClose) && (
          <div className="oz-modal__header">
            {title && <h2 className="oz-modal__title">{title}</h2>}
            {showClose && (
              <button
                className="oz-modal__close"
                onClick={onClose}
                aria-label="Close modal"
              >
                <X size={20} />
              </button>
            )}
          </div>
        )}

        {/* Content */}
        <div className="oz-modal__content">
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div className="oz-modal__footer">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

export default Modal;
